import { Injectable, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HubConnectionState } from '@microsoft/signalr';
import { AuthService } from '@sia/auth';
import {
  SiaBaseActionService,
  SiaCacheDatabaseService,
  SiaComponentConfigService,
  SiaI18nService,
  SiaNewRouterService,
  SiaParamsAndPatternsService,
  SiaSessionService,
  SiaSignalrService,
  SiaSizeDetectorService,
  SiaUsersService,
  notify,
} from '@sia/shared';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { SiaActionsApi } from '../apis/sia-actions-api';
import { SiaAction } from '../interfaces/sia-action';
import { SiaActionFired } from '../interfaces/sia-action-fired';
import { SiaItemAction } from '../interfaces/sia-item-action';
import { SiaItemWithActions } from '../interfaces/sia-item-with-actions';

@Injectable({
  providedIn: 'root',
})
export class SiaActionsService {
  #siaParamsAndPatternsService = inject(SiaParamsAndPatternsService);
  #componentConfigService = inject(SiaComponentConfigService);
  #cacheDatabaseService = inject(SiaCacheDatabaseService);
  #siaBaseActionService = inject(SiaBaseActionService);
  #sizeDetector = inject(SiaSizeDetectorService);
  #routerService = inject(SiaNewRouterService);
  #signalrService = inject(SiaSignalrService);
  #sessionService = inject(SiaSessionService);
  #i18nService = inject(SiaI18nService);
  #userService = inject(SiaUsersService);
  #authService = inject(AuthService);
  #api = inject(SiaActionsApi);
  #dialog = inject(MatDialog);

  #subs: Subscription[] = [];
  #paramsChangedSubject: Subject<boolean> = new Subject<boolean>();
  #googleUserAuthSubject: Subject<boolean> = new Subject<boolean>();
  #googleUserLogoutSubject: Subject<boolean> = new Subject<boolean>();
  #actionDotNotificationSubject: ReplaySubject<{
    componentId?: string;
    actionId?: string;
    type?: string;
  }> = new ReplaySubject<{
    componentId?: string;
    actionId?: string;
    type?: string;
  }>();
  #firedActionsSubject: Subject<SiaActionFired> = new Subject<SiaActionFired>();
  #actionVisibleFire: Subject<{
    componentId?: string;
    actionId?: string;
  }> = new Subject<{ componentId?: string; actionId?: string }>();

  // track whether the current layout is in a popup outlet
  #isPopup = signal<string | undefined>(undefined);
  readonly isPopup = computed(() => this.#isPopup());

  setPopup(value: string | undefined) {
    this.#isPopup.set(value);
  }

  constructor() {
    let s0 = this.#siaParamsAndPatternsService.paramsHardUpdated$.subscribe(
      () => {
        this.actionParamsChanged();
      },
    );
    this.#subs.push(s0);
    let s = this.#siaParamsAndPatternsService.departmentChanged$.subscribe(
      () => {
        this.actionParamsChanged();
      },
    );
    this.#subs.push(s);
  }

  get googleUserAuthSubject$() {
    return this.#googleUserAuthSubject.asObservable();
  }

  get googleUserLogoutSubject$() {
    return this.#googleUserLogoutSubject.asObservable();
  }

  async onExecAction(action: string, id: any): Promise<boolean> {
    switch (action) {
      default:
        return false;
    }
  }

  /**
   * Dato un componente e la modalità operativa (READ, EDIT, CREATE)
   * ritorna le azioni abilitate in base ai privilegi
   * @param componentId
   * @param mode
   * @returns
   */
  async getActionsByComponent(
    componentId: string,
    mode?: string,
  ): Promise<SiaAction[]> {
    if (!componentId) return [];

    const cached = await this.#cacheDatabaseService.getActions(
      componentId,
      mode,
    );
    if (cached) {
      return cached;
    }

    const actions = await this.#api.getActionsByComponent(componentId, mode);
    actions?.forEach((x) => (x.componentId = componentId));
    await this.#cacheDatabaseService.setActions(componentId, actions, mode);
    return actions;
  }

  /**
   * Visualizza un dot colorato su una action specifica di un componente
   * @param componentId Il componente interessato
   * @param actionId l'azione interessata
   * @param type il tipo di dot da visualizzare sul action (info, problem, warning)
   */
  setActionNotificationDot(
    componentId: string,
    actionId: string,
    type: string,
  ) {
    this.#actionDotNotificationSubject.next({ componentId, actionId, type });
  }

  /**
   * Notifica la action da gestire
   * @returns
   */
  visibilityActions$() {
    return this.#siaBaseActionService.visibilityActionsSubject.asObservable();
  }

  actionParamsChanged$() {
    return this.#paramsChangedSubject.asObservable();
  }

  notifyActionDot$() {
    return this.#actionDotNotificationSubject.asObservable();
  }

  fireSiaAction(action: SiaActionFired) {
    if (!action) return;
    this.#firedActionsSubject.next(action);
    switch (action.action?.id) {
      case 'back':
        this.#routerService.navigateBack();
        break;
      case 'print':
        window.print();
        break;
      case 'change-password':
        this.#authService.changePasswordPopUp();
        break;
      case 'duplicate-site':
        //open a new tab with the same site
        window.open(window.location.href.split('#')[0], '_blank');
        break;
      case 'manage-actions':
        this.#routerService.navigateMenuWithParams('actions-manager', [
          {
            id: 'componentId',
            value: action.componentId,
          },
        ]);
        break;
      case 'manage-permissions':
        this.#routerService.navigateMenuWithParams(
          'popup^component-permissions',
          [
            {
              id: 'componentId',
              value: action.componentId,
            },
          ],
        );
        break;
      case 'current-component':
        notify(action.componentId ?? '', 2000, 'success');
        break;
      case 'manage-menu':
        this.#routerService.navigateMenuWithParams('popup^crud-menu', [
          {
            id: 'id',
            value: action.componentId,
          },
        ]);
        break;
      case 'signalr':
        this.checkSignalrService();
        break;
      case 'google-login':
        this.#googleUserAuthSubject.next(true);
        break;
      case 'google-logout':
        this.#googleUserLogoutSubject.next(true);
        break;
      case 'page-configure':
        this.#routerService.navigateMenuWithParams('close^page-configure', [
          {
            id: 'componentId',
            value: action.componentId,
          },
        ]);
        break;
      default:
        break;
    }
  }

  fireActionIfVisible(actionId: string, key: string | undefined) {
    this.#actionVisibleFire.next({ componentId: key, actionId: actionId });
  }

  fireActionIfVisible$() {
    return this.#actionVisibleFire.asObservable();
  }

  private checkSignalrService() {
    var state = this.#signalrService.getState();
    if (!state) {
      notify('Servizio RealTime non disponibile', 5000, 'error');
    }
    switch (state) {
      case HubConnectionState.Connected:
        notify('Servizio RealTime connesso', 3000, 'success');
        break;
      case HubConnectionState.Connecting:
        notify('Servizio RealTime in connessione', 2000, 'warning');
        break;
      case HubConnectionState.Disconnected:
        // notify("Servizio RealTime disconnesso", "error", 3000);
        this.#signalrService.connect(this.#userService.getUserData()?.id ?? 0);
        break;
      case HubConnectionState.Disconnecting:
        notify('Servizio RealTime in disconnessione', 2000, 'warning');
        break;
      case HubConnectionState.Reconnecting:
        notify('Servizio RealTime in riconnessione', 2000, 'warning');
        break;
    }
  }

  actionFired$() {
    return this.#firedActionsSubject.asObservable();
  }

  actionParamsChanged() {
    this.#paramsChangedSubject.next(true);
  }

  async getItemWithActions(): Promise<SiaItemWithActions[]> {
    return this.#api.getItemWithActions();
  }

  async getActionsByItemAndType(
    id: string,
    type?: string,
  ): Promise<SiaItemAction[]> {
    return this.#api.getActionsByItemAndType(id, type);
  }

  async getActions() {
    return this.#api.getActions();
  }

  /**
   * Salva un set di azioni lato server
   * @param actions l'array con le azioni da salvare
   * @param id indica l'id del componente o del gruppo di actions a cui il set di azioni si riferisce
   * @param isGroup indica se l'id si riferisce ad un gruppo
   * @returns
   */
  async saveActions(
    actions: SiaItemAction[],
    id: string,
    isGroup: boolean,
  ): Promise<boolean> {
    const done = await this.#api.saveActions(actions, id, isGroup);
    if (isGroup) {
      await this.#cacheDatabaseService.clearAllActions();
    } else {
      const clears = [
        this.#cacheDatabaseService.clearActions(id, 'read'),
        this.#cacheDatabaseService.clearActions(id, 'update'),
        this.#cacheDatabaseService.clearActions(id, 'create'),
      ];
      await Promise.all(clears);
    }
    return done;
  }
  /**
   * Salva un set di azioni lato server
   * @param actions l'array con le azioni da salvare
   * @param id indica l'id del componente
   * @returns
   */
  async renameActions(actions: SiaItemAction[], id: string, group: boolean) {
    return this.#api.renameActions(actions, id, group);
  }

  async savePageActionConfiguration(
    id: string,
    masterConf: string,
    mobileConf: string,
  ) {
    return this.#api.savePageActionConfiguration(id, masterConf, mobileConf);
  }

  ngOnDestroy() {
    this.#subs.forEach((x) => x.unsubscribe());
  }
}
